import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Spinner } from "../Bits";

export default function SocialCardsAdmin() {
  const [plants, setPlants] = useState([]);
  const [articles, setArticles] = useState([]);
  const [template, setTemplate] = useState("plant"); // plant | article | quote
  const [format, setFormat] = useState("post");
  const [carousel, setCarousel] = useState(false);
  const [aiImage, setAiImage] = useState(false);
  const [plantId, setPlantId] = useState("");
  const [articleId, setArticleId] = useState("");
  const [headline, setHeadline] = useState("");
  const [subtext, setSubtext] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [handle, setHandle] = useState("@adventurecouplelu");
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  // Caption
  const [tone, setTone] = useState("warm");
  const [cta, setCta] = useState("tour");
  const [topic, setTopic] = useState("");
  const [genCaption, setGenCaption] = useState(false);
  const [caption, setCaption] = useState(null); // {caption, hashtags, fallback}
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/plants").then((r) => setPlants(r.data || []));
    api.get("/admin/articles").then((r) => setArticles(r.data || []));
  }, []);

  const generate = async () => {
    setGenerating(true);
    setResults([]);
    try {
      const r = await api.post("/admin/social/generate", {
        template, format, plant_id: plantId || null, article_id: articleId || null,
        headline, subtext, image_url: imageUrl, handle, carousel, ai_image: aiImage,
      });
      setResults(r.data.slides || []);
      toast.success(`${r.data.count} Bild${r.data.count > 1 ? "er" : ""} erstellt`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler bei der Generierung");
    } finally {
      setGenerating(false);
    }
  };

  const generateCaption = async () => {
    setGenCaption(true);
    setCaption(null);
    try {
      const r = await api.post("/admin/social/caption", {
        template: template === "quote" ? "topic" : template,
        plant_id: plantId || null,
        article_id: articleId || null,
        topic: topic || headline,
        tone, cta,
      });
      setCaption(r.data);
      if (r.data.fallback) {
        toast.info("Fallback-Caption (LLM-Budget leer). Lade auf für KI-Texte.");
      } else {
        toast.success("Caption erstellt");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setGenCaption(false);
    }
  };

  // One-click: Article → 3-Slide Carousel + Caption in parallel
  const autoArticleCarousel = async () => {
    if (!articleId) {
      toast.error("Bitte zuerst einen Artikel auswählen");
      return;
    }
    setGenerating(true);
    setGenCaption(true);
    setResults([]);
    setCaption(null);
    try {
      const [slidesRes, captionRes] = await Promise.all([
        api.post("/admin/social/generate", {
          template: "article", format, article_id: articleId, carousel: true,
          headline, subtext, image_url: imageUrl, handle,
        }),
        api.post("/admin/social/caption", {
          template: "article", article_id: articleId, tone, cta,
        }),
      ]);
      setResults(slidesRes.data.slides || []);
      setCaption(captionRes.data);
      toast.success(`Magazin-Carousel erstellt: ${slidesRes.data.count} Slides + Caption`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Magazin-Carousel");
    } finally {
      setGenerating(false);
      setGenCaption(false);
    }
  };

  const copyCaption = async () => {
    if (!caption) return;
    const text = `${caption.caption}\n\n${caption.hashtags}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("In Zwischenablage kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const downloadAll = async () => {
    for (const s of results) {
      const a = document.createElement("a");
      a.href = `${apiBase}${s.url}`;
      a.download = `wildkraeuter-${Date.now()}-${s.slide}.jpg`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      await new Promise((r) => setTimeout(r, 500));
    }
  };

  return (
    <div data-testid="admin-social">
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
        {/* Form */}
        <div className="plant-card p-6 space-y-5">
          <div>
            <label className="overline">Vorlage</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                { v: "plant", l: "Pflanze" },
                { v: "article", l: "Artikel" },
                { v: "quote", l: "Freitext / Zitat" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setTemplate(opt.v)}
                  className={`px-3 py-1.5 rounded-full text-xs border ${template === opt.v ? "bg-[var(--wp-primary)] text-[var(--wp-cream)] border-[var(--wp-primary)]" : ""}`}
                  style={template !== opt.v ? { borderColor: "var(--wp-border)" } : {}}
                  data-testid={`social-template-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="overline">Format</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                { v: "post", l: "Post 1:1 (1080×1080)" },
                { v: "portrait", l: "Portrait 4:5 (1080×1350)" },
                { v: "story", l: "Story 9:16 (1080×1920)" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setFormat(opt.v)}
                  className={`px-3 py-1.5 rounded-full text-xs border ${format === opt.v ? "bg-[var(--wp-primary)] text-[var(--wp-cream)] border-[var(--wp-primary)]" : ""}`}
                  style={format !== opt.v ? { borderColor: "var(--wp-border)" } : {}}
                  data-testid={`social-format-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          {template === "plant" && (
            <>
              <div>
                <label className="overline">Pflanze auswählen</label>
                <select value={plantId} onChange={(e) => {
                  setPlantId(e.target.value);
                  const p = plants.find((pp) => pp.id === e.target.value);
                  if (p) { setHeadline(p.name_de); setSubtext(p.short_description || ""); }
                }} className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="social-plant-select">
                  <option value="">– bitte wählen –</option>
                  {plants.map((p) => <option key={p.id} value={p.id}>{p.name_de} ({p.category}{p.images?.length ? ` · ${p.images.length + 1} Bilder` : ""})</option>)}
                </select>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={carousel} onChange={(e) => setCarousel(e.target.checked)} className="mt-1" data-testid="social-carousel" />
                <div>
                  <div className="font-medium">Carousel-Modus</div>
                  <div className="text-xs opacity-70">Erstellt bis zu 6 Slides (1 pro Pflanzenbild). Lädt automatisch alle Galerie-Bilder.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={aiImage} onChange={(e) => setAiImage(e.target.checked)} className="mt-1" data-testid="social-ai-image" />
                <div>
                  <div className="font-medium">KI-Bild speziell für diese Karte erstellen</div>
                  <div className="text-xs opacity-70">Statt der Datenbank-Bilder erstellt Nano Banana ein frisches, pflanzenspezifisches Close-up-Foto. Empfohlen, wenn die DB-Bilder generisch sind.</div>
                </div>
              </label>
            </>
          )}

          {template === "article" && (
            <>
              <div>
                <label className="overline">Artikel auswählen</label>
                <select value={articleId} onChange={(e) => {
                  setArticleId(e.target.value);
                  const a = articles.find((aa) => aa.id === e.target.value);
                  if (a) { setHeadline(a.title); setSubtext(a.excerpt || ""); }
                }} className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="social-article-select">
                  <option value="">– bitte wählen –</option>
                  {articles.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                </select>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={carousel} onChange={(e) => setCarousel(e.target.checked)} className="mt-1" data-testid="social-article-carousel" />
                <div>
                  <div className="font-medium">3-Slide-Carousel (Hook · Insight · CTA)</div>
                  <div className="text-xs opacity-70">Erstellt automatisch 3 thematische Slides aus dem Artikel: Cover/Titel, Kern-Insight via Claude, CTA mit Link.</div>
                </div>
              </label>
            </>
          )}

          {template === "quote" && (
            <div>
              <label className="overline">Hintergrundbild (optional)</label>
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://… oder leer für reines Branding"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid="social-quote-image"
              />
            </div>
          )}

          <div>
            <label className="overline">Headline</label>
            <input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="z.B. Bärlauch im Mai" className="w-full mt-1 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="social-headline" />
          </div>

          <div>
            <label className="overline">Untertext (max ca. 80 Zeichen)</label>
            <textarea value={subtext} onChange={(e) => setSubtext(e.target.value)} placeholder="Erkennen, sammeln, genießen – sicher und nachhaltig" className="w-full mt-1 px-3 py-2 rounded-lg border text-sm min-h-[60px]" style={{ borderColor: "var(--wp-border)" }} data-testid="social-subtext" />
          </div>

          <div>
            <label className="overline">Instagram-Handle</label>
            <input value={handle} onChange={(e) => setHandle(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} />
          </div>

          <button
            onClick={generate}
            disabled={generating || (template === "plant" && !plantId) || (template === "article" && !articleId) || (template === "quote" && !headline)}
            className="btn-primary w-full justify-center"
            data-testid="social-generate"
          >
            <i className="fa-solid fa-sparkles text-xs" /> {generating ? "Generiere …" : carousel ? "Carousel erstellen" : "Bild erstellen"}
          </button>

          {template === "article" && (
            <button
              onClick={autoArticleCarousel}
              disabled={generating || genCaption || !articleId}
              className="w-full justify-center inline-flex items-center gap-2 rounded-full py-3 px-6 text-sm font-medium transition-colors mt-2"
              style={{
                backgroundColor: "var(--wp-olive)",
                color: "var(--wp-cream)",
                opacity: (generating || genCaption || !articleId) ? 0.6 : 1,
              }}
              data-testid="social-auto-article"
              title="Erstellt mit 1 Klick: 3 thematische Slides (Hook · Insight · CTA) + Caption + Hashtags"
            >
              <i className="fa-solid fa-wand-magic-sparkles text-xs" />
              {generating || genCaption ? "Magazin-Carousel wird erstellt …" : "Auto: Magazin → 3-Slide Carousel + Caption"}
            </button>
          )}

          {/* Caption Generator Section */}
          <div className="pt-5 mt-2 border-t" style={{ borderColor: "var(--wp-border)" }}>
            <div className="overline mb-3 flex items-center gap-2">
              <i className="fa-brands fa-instagram" />
              Instagram-Caption (mit Claude)
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>
                <label className="text-xs opacity-70">Tonalität</label>
                <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "var(--wp-border)" }} data-testid="caption-tone">
                  <option value="warm">Warm & persönlich</option>
                  <option value="informativ">Informativ</option>
                  <option value="locker">Locker / lustig</option>
                  <option value="poetisch">Poetisch</option>
                </select>
              </div>
              <div>
                <label className="text-xs opacity-70">Call-to-Action</label>
                <select value={cta} onChange={(e) => setCta(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "var(--wp-border)" }} data-testid="caption-cta">
                  <option value="tour">Tour buchen</option>
                  <option value="website">Website</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="none">Keine</option>
                </select>
              </div>
            </div>
            {template === "quote" && (
              <input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={`Thema/Idee für Caption (z.B. "Mai-Spaziergang")`}
                className="w-full mb-3 px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid="caption-topic"
              />
            )}
            <button
              type="button"
              onClick={generateCaption}
              disabled={genCaption || (template === "plant" && !plantId) || (template === "article" && !articleId) || (template === "quote" && !topic && !headline)}
              className="btn-secondary w-full justify-center !py-2.5"
              data-testid="caption-generate"
            >
              <i className="fa-solid fa-pen-to-square text-xs" /> {genCaption ? "Schreibe Caption …" : "Caption + Hashtags generieren"}
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          {results.length === 0 && !generating && (
            <div className="plant-card p-12 text-center opacity-70" data-testid="social-empty">
              <i className="fa-brands fa-instagram text-5xl opacity-30 mb-4 block" />
              <p className="font-display text-xl mb-2">Bereit für Instagram</p>
              <p className="text-sm">Wähle eine Vorlage links und klicke «Bild erstellen". Das Ergebnis erscheint hier zum Download.</p>
            </div>
          )}

          {generating && (
            <div className="plant-card p-12 text-center" data-testid="social-loading">
              <Spinner label="Erstelle deine Social-Cards …" />
            </div>
          )}

          {results.length > 0 && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm opacity-70">{results.length} Bild{results.length > 1 ? "er" : ""} bereit</div>
                {results.length > 1 && (
                  <button onClick={downloadAll} className="btn-secondary !py-2 !px-4 text-sm" data-testid="social-download-all">
                    <i className="fa-solid fa-download text-xs" /> Alle herunterladen
                  </button>
                )}
              </div>
              <div className={`grid ${results.length === 1 ? "" : "grid-cols-2 md:grid-cols-3"} gap-3`}>
                {results.map((r, i) => (
                  <div key={i} className="plant-card overflow-hidden" data-testid={`social-result-${i}`}>
                    <img src={`${apiBase}${r.url}`} alt={`Slide ${r.slide}`} className="w-full" />
                    <div className="p-2 flex items-center justify-between">
                      <span className="text-xs opacity-60">Slide {r.slide}</span>
                      <a
                        href={`${apiBase}${r.url}`}
                        download={`wildkraeuter-${r.slide}.jpg`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary !py-1 !px-3 text-xs"
                        data-testid={`social-download-${i}`}
                      >
                        Download
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-xs opacity-60 p-3 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }}>
                💡 <strong>Tipp:</strong> Lade die Bilder herunter und poste sie über die Instagram-App. Carousel-Posts: alle Bilder zusammen hochladen, Reihenfolge per Drag.
              </div>
            </>
          )}

          {caption && (
            <div className="plant-card p-5 space-y-3" data-testid="caption-result">
              <div className="flex items-center justify-between">
                <div className="overline flex items-center gap-2"><i className="fa-brands fa-instagram" /> Caption</div>
                <button onClick={copyCaption} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="caption-copy">
                  <i className="fa-solid fa-copy text-xs" /> Kopieren
                </button>
              </div>
              {caption.fallback && (
                <div className="text-xs p-2 rounded" style={{ backgroundColor: "rgba(207,140,69,0.12)" }}>
                  ⚠ LLM-Budget leer – Fallback-Caption. Lade auf für KI-generierte Texte.
                </div>
              )}
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{caption.caption}</div>
              <div className="text-xs opacity-70 break-words leading-relaxed pt-3 border-t" style={{ borderColor: "var(--wp-border)" }}>
                {caption.hashtags}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
